import { SET } from "@/stores/CommumActionTypes";
import {
	SET_HOME_TRENDS, SET_PARK_TRENDS, SET_SELECTED_ASSETS, TOGGLE_SELECT_FILTERED_ASSETS, SET_SELECTED_ASSETS_MODEL,
	SHOW_TOAST, RESET_STATE, GET_CURRENT_TURBINE
} from "./TrendAssetsActionType";
import i18n from "@/i18n";
import {
	TREND_PATH,
	TREND_ASSET_SELECTOR_PATH,
	TREND_SIGNAL_SETTINGS_PATH,
	FREQUENCY_TREND_SIGNAL_PATH,
	ADVANCED_SETTINGS_DRAWER_PATH,
	CUSTOM_DOMAIN_PATH,
	STRATIFICATION_PATH
} from "../../ModulePath.js";

const actions = {
	/**
	 * Define se há ativos selecionados via query string (parâmetro `assets`) ao entrar na rota.
	 * Utilizada para indicar que a tela deve exibir tendências relacionadas a ativos selecionados.
	 * Chamado via `beforeRouteEnter`.
	 */
	[SET_HOME_TRENDS]: ({ commit }, query) =>
	{
		commit(SET_HOME_TRENDS, !!query.assets);
	},
	/**
	 * Chama o reset do estado do módulo de ativos de tendência.
	 */
	[RESET_STATE]: ({ commit }) =>
	{
		commit("RESET_STATE");
	},

	/**
	 * Define se há ativos de parque selecionados via query string (`park_asset`) ao entrar na rota.
	 * Usado para controlar a exibição de tendências específicas para ativos de parque.
	 * Chamado via `beforeRouteEnter`.
	 */
	[SET_PARK_TRENDS]: ({ commit }, query) =>
	{
		commit(SET_PARK_TRENDS, !!query.park_asset);
	},

	/**
	 * Define a lista completa de ativos disponíveis.
	 * @param {Object} context - Contexto do Vuex.
	 * @param {Array} assets - Lista de ativos.
	 */
	[SET]: ({ commit }, assets) =>
	{
		commit(SET, assets);
	},

	/**
	 * Define os ativos atualmente selecionados.
	 * @param {Object} context - Contexto do Vuex.
	 * @param {Array} selectedAssets - Lista de ativos selecionados.
	 */
	[SET_SELECTED_ASSETS]: ({ commit }, selectedAssets) =>
	{
		commit(SET_SELECTED_ASSETS, selectedAssets);
	},

	/**
	 * Alterna a seleção dos ativos filtrados.
	 * Se todos os ativos filtrados já estão selecionados, eles são desmarcados.
	 * Caso contrário, os ativos filtrados ainda não selecionados são adicionados à seleção.
	 * @param {Object} context - Contexto do Vuex com commit, state e getters.
	 */
	[TOGGLE_SELECT_FILTERED_ASSETS]: async({
		commit,
		dispatch,
		state,
		getters
	}) =>
	{
		await dispatch(`${FREQUENCY_TREND_SIGNAL_PATH}/${RESET_STATE}`, null, { root: true });
		await dispatch(`${TREND_SIGNAL_SETTINGS_PATH}/${RESET_STATE}`, null, { root: true });

		const filtered = getters.filteredAssets;

		const filteredNames = new Set(filtered.map(f => f.name));
		const selectedNamesSet = new Set(state.selectedAssets.map(selected => selected.name));

		const allFilteredSelected = filtered.every(asset => selectedNamesSet.has(asset.name));

		if (allFilteredSelected)
		{
			const remainingSelectedAssets = state.selectedAssets.filter(
				selected => !filteredNames.has(selected.name)
			);
			commit(SET_SELECTED_ASSETS, remainingSelectedAssets);
		}
		else
		{
			const newAssetsToSelect = filtered.filter(asset => !selectedNamesSet.has(asset.name));
			const updatedSelectedAssets = [ ...state.selectedAssets, ...newAssetsToSelect ];
			commit(SET_SELECTED_ASSETS, updatedSelectedAssets);
		}
	},

	/**
	 * Atualiza os ativos selecionados com base nos IDs recebidos.
	 * Também reseta o estado de diversos módulos relacionados ao menu de tendências.
	 * @param {Object} context - Contexto do Vuex com dispatch, state e commit.
	 * @param {Array} newSelectedAssetIds - Lista de IDs de ativos a serem selecionados.
	 */
	[SET_SELECTED_ASSETS_MODEL]: async({
		dispatch, state, commit
	}, newSelectedAssetIds) =>
	{
		const allAvailableAssets = state.assets;
	
		try
		{
			if (!allAvailableAssets || allAvailableAssets.length === 0)
			{
				await dispatch(`${TREND_ASSET_SELECTOR_PATH}/SET_SELECTED_ASSETS`, [], { root: true });
			}
			else
			{
				const selectedAssetObjects = allAvailableAssets.filter(asset => newSelectedAssetIds.includes(asset.id));
				await dispatch(`${TREND_ASSET_SELECTOR_PATH}/SET_SELECTED_ASSETS`, selectedAssetObjects, { root: true });
			}
		}
		catch (error)
		{
			const errorMessage = i18n.t("ErrorMessages.TrendAssetSelectorError");
			commit(`${TREND_ASSET_SELECTOR_PATH}/${SHOW_TOAST}`, { message: errorMessage, type: "error" }, { root: true });
		}
	
		await Promise.all([
			dispatch(`${TREND_SIGNAL_SETTINGS_PATH}/RESET_STATE`, null, { root: true }),
			dispatch(`${FREQUENCY_TREND_SIGNAL_PATH}/RESET_STATE`, null, { root: true }),
			dispatch(`${TREND_PATH}/RESET_STATE`, null, { root: true }),
			dispatch(`${ADVANCED_SETTINGS_DRAWER_PATH}/TOGGLE_CUSTOM_DOMAIN_COMPARISON`, false, { root: true }),
			dispatch(`${ADVANCED_SETTINGS_DRAWER_PATH}/TOGGLE_CUSTOM_DOMAIN`, false, { root: true }),
			dispatch(`${ADVANCED_SETTINGS_DRAWER_PATH}/TOGGLE_STRATIFICATION`, false, { root: true }),
			dispatch(`${CUSTOM_DOMAIN_PATH}/RESET_STATE`, null, { root: true }),
			dispatch(`${STRATIFICATION_PATH}/RESET_STATE`, null, { root: true })
		]);
	},

	[GET_CURRENT_TURBINE]: async({ state, commit }, turbineRouteId) =>
	{
		const asset =  state.AvailableAssets.find(el => el.id === turbineRouteId);
		if (asset)
		{
			commit(SET_SELECTED_ASSETS, [ asset ]);
		}
	}
	
};

export default { actions };
